import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useParams } from 'react-router-dom'
import { Check, ChevronDown, Loader2 } from 'lucide-react'
import { PeriodSelector } from '@/components/analytics/PeriodSelector'
import { registerUnsavedChangesGuard } from '@/lib/unsavedChanges'
import { BUSINESS_HEALTH_GROUPS, emptyBusinessHealthAnswers, normalizeBusinessHealthAnswers, type BusinessHealthAnswer, type BusinessHealthQuestion } from '@/lib/businessHealthChecklist'
import { getBusinessHealth, type BusinessHealthRecord, saveBusinessHealth } from '@/services/businessHealthService'

const ANSWER_OPTIONS: Array<{ value: Exclude<BusinessHealthAnswer, null>; label: string }> = [
  { value: 'yes', label: 'Да' },
  { value: 'no', label: 'Нет' },
  { value: 'partial', label: 'Частично' },
]
const ANSWER_MENU_WIDTH = 128
const ANSWER_MENU_HEIGHT = 100

function groupQuestionsByMetric(questions: BusinessHealthQuestion[]) {
  const groups: Array<{ primaryMetric: string | null; questions: BusinessHealthQuestion[] }> = []

  for (const question of questions) {
    if (question.primaryMetric || groups.length === 0) {
      groups.push({ primaryMetric: question.primaryMetric ?? null, questions: [question] })
    } else {
      groups[groups.length - 1].questions.push(question)
    }
  }

  return groups
}

function answerPoints(answer: BusinessHealthAnswer): number {
  if (answer === 'yes') return 1
  if (answer === 'partial') return 0.5
  return 0
}

function answerTone(answer: BusinessHealthAnswer): string {
  if (answer === 'yes') return 'border-emerald-600/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
  if (answer === 'partial') return 'border-amber-600/30 bg-amber-500/10 text-amber-700 dark:text-amber-300'
  if (answer === 'no') return 'border-rose-600/30 bg-rose-500/10 text-rose-700 dark:text-rose-300'
  return 'border-border bg-muted/40 text-muted-foreground'
}

function formatPoints(points: number): string {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(points)
}

function formatSavedAt(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('ru-RU', { dateStyle: 'medium' }).format(date)
}

function errorText(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) return String(error.message)
  return 'Не удалось загрузить чек-лист. Попробуйте обновить страницу.'
}

function scopedAnswers(studio: unknown, partner: unknown, storageScope: BusinessHealthRecord['storage_scope']): Record<string, BusinessHealthAnswer> {
  const studioAnswers = normalizeBusinessHealthAnswers(studio)
  if (storageScope === 'legacy_studio') return studioAnswers
  const partnerAnswers = normalizeBusinessHealthAnswers(partner)
  for (const question of BUSINESS_HEALTH_GROUPS.find(group => group.id === 'entrepreneur')?.questions ?? []) {
    studioAnswers[question.id] = partnerAnswers[question.id]
  }
  return studioAnswers
}

export function StudioBusinessHealthPage() {
  const { studioId } = useParams<{ partnerNumber: string; studioId: string }>()
  const studioInternalId = Number(studioId)
  const [record, setRecord] = useState<BusinessHealthRecord | null>(null)
  const [selectedReviewId, setSelectedReviewId] = useState<number | null>(null)
  const [selectedPartnerReviewId, setSelectedPartnerReviewId] = useState<number | null>(null)
  const [selectedPeriodId, setSelectedPeriodId] = useState<number | null>(null)
  const [selectedWeekId, setSelectedWeekId] = useState<number | null>(null)
  const [draft, setDraft] = useState<Record<string, BusinessHealthAnswer>>(emptyBusinessHealthAnswers)
  const [openAnswerMenu, setOpenAnswerMenu] = useState<{ questionId: string; left: number; top: number } | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedMessage, setSavedMessage] = useState<string | null>(null)

  const refresh = useCallback(async (preserveWeekId?: number) => {
    if (!Number.isInteger(studioInternalId) || studioInternalId <= 0) {
      setError('Не удалось определить студию.')
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)
    try {
      const next = await getBusinessHealth(studioInternalId)
      setRecord(next)
      const targetWeekId = preserveWeekId ?? next.current_week_id
      const targetWeek = next.calendar_weeks.find(week => week.week_id === targetWeekId)
      const targetReview = next.history.find(review => review.week_id === targetWeekId) ?? null
      const targetPartnerReview = next.partner_history.find(review => review.week_id === targetWeekId) ?? null
      setSelectedReviewId(targetReview?.review_id ?? null)
      setSelectedPartnerReviewId(targetPartnerReview?.review_id ?? null)
      setSelectedPeriodId(targetWeek?.period_id ?? next.current_period.period_id)
      setSelectedWeekId(targetWeekId)
      setDraft(scopedAnswers(targetReview?.answers, targetPartnerReview?.answers, next.storage_scope))
    } catch (loadError) {
      setError(errorText(loadError))
    } finally {
      setIsLoading(false)
    }
  }, [studioInternalId])

  useEffect(() => { void refresh() }, [refresh])

  const activePeriodId = selectedPeriodId ?? record?.current_period.period_id ?? null
  const selectedPeriod = record?.periods.find(period => period.period_id === activePeriodId) ?? record?.current_period ?? null
  const selectedPeriodIndex = record?.periods.findIndex(period => period.period_id === activePeriodId) ?? -1
  const periodWeeks = record?.calendar_weeks.filter(week => week.period_id === activePeriodId) ?? []
  const selectedReview = useMemo(() => {
    if (!record || selectedReviewId === null) return null
    return record.history.find(review => review.review_id === selectedReviewId) ?? null
  }, [record, selectedReviewId])
  const selectedPartnerReview = useMemo(() => {
    if (!record || selectedPartnerReviewId === null) return null
    return record.partner_history.find(review => review.review_id === selectedPartnerReviewId) ?? null
  }, [record, selectedPartnerReviewId])
  const activeWeekId = selectedWeekId ?? record?.current_week_id ?? null
  const activeWeek = periodWeeks.find(week => week.week_id === activeWeekId) ?? null
  const weekHistory = record?.history.filter(review => review.week_id === activeWeekId) ?? []
  const partnerWeekHistory = record?.partner_history.filter(review => review.week_id === activeWeekId) ?? []
  const latestWeekReview = weekHistory[0] ?? null
  const latestPartnerWeekReview = partnerWeekHistory[0] ?? null
  const isHistoricalPeriodSelected = activePeriodId !== record?.current_period.period_id
  const isCurrentVersionSelected = (selectedReviewId === null || selectedReviewId === latestWeekReview?.review_id)
    && (selectedPartnerReviewId === null || selectedPartnerReviewId === latestPartnerWeekReview?.review_id)
  const canEdit = Boolean(record?.can_edit && !isHistoricalPeriodSelected && isCurrentVersionSelected)
  const currentAnswers = useMemo(
    () => scopedAnswers(latestWeekReview?.answers, latestPartnerWeekReview?.answers, record?.storage_scope ?? 'partner'),
    [latestWeekReview, latestPartnerWeekReview, record?.storage_scope],
  )
  const hasChanges = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(currentAnswers),
    [currentAnswers, draft],
  )

  useEffect(() => registerUnsavedChangesGuard(() => {
    if (!canEdit || !hasChanges) return true
    if (isSaving) {
      window.alert('Чек-лист сохраняется. Дождитесь завершения сохранения.')
      return false
    }
    return window.confirm('В чек-листе есть несохранённые ответы. При выходе они могут потеряться. Выйти без сохранения?')
  }), [canEdit, hasChanges, isSaving])

  useEffect(() => {
    if (!canEdit || !hasChanges) return
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warnBeforeUnload)
    return () => window.removeEventListener('beforeunload', warnBeforeUnload)
  }, [canEdit, hasChanges])

  useEffect(() => {
    if (!openAnswerMenu) return
    const closeMenu = () => setOpenAnswerMenu(null)
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (event.target instanceof Element && event.target.closest('[data-business-health-answer-control]')) return
      closeMenu()
    }
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') closeMenu() }
    document.addEventListener('pointerdown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)
    window.addEventListener('scroll', closeMenu, true)
    window.addEventListener('resize', closeMenu)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
      window.removeEventListener('scroll', closeMenu, true)
      window.removeEventListener('resize', closeMenu)
    }
  }, [openAnswerMenu])

  const totalQuestionCount = BUSINESS_HEALTH_GROUPS.reduce((total, group) => total + group.questions.length, 0)
  const filledQuestionCount = BUSINESS_HEALTH_GROUPS.reduce(
    (total, group) => total + group.questions.filter(question => draft[question.id] !== null).length,
    0,
  )
  const totalPoints = useMemo(
    () => BUSINESS_HEALTH_GROUPS.reduce(
      (total, group) => total + group.questions.reduce((groupTotal, question) => groupTotal + answerPoints(draft[question.id]), 0),
      0,
    ),
    [draft],
  )

  const confirmDiscardDraft = () => {
    if (!canEdit || !hasChanges) return true
    if (isSaving) {
      window.alert('Чек-лист сохраняется. Дождитесь завершения сохранения.')
      return false
    }
    return window.confirm('Есть несохранённые ответы. При переключении периода, недели или версии они потеряются. Продолжить?')
  }

  const selectPeriod = (periodId: number | undefined) => {
    if (!record || periodId === undefined || periodId === activePeriodId || !confirmDiscardDraft()) return
    const weeks = record.calendar_weeks.filter(week => week.period_id === periodId && week.is_available)
    const targetWeek = weeks[weeks.length - 1]
    if (!targetWeek) return
    setSelectedPeriodId(periodId)
    setSelectedWeekId(targetWeek.week_id)
    setSelectedReviewId(null)
    setSelectedPartnerReviewId(null)
  }

  useEffect(() => {
    if (!record) return
    const review = selectedReviewId === null
      ? latestWeekReview
      : record.history.find(item => item.review_id === selectedReviewId) ?? null
    const partnerReview = selectedPartnerReviewId === null
      ? latestPartnerWeekReview
      : record.partner_history.find(item => item.review_id === selectedPartnerReviewId) ?? null
    setDraft(scopedAnswers(review?.answers, partnerReview?.answers, record.storage_scope))
    setSavedMessage(null)
  }, [record, selectedReviewId, selectedPartnerReviewId, latestWeekReview, latestPartnerWeekReview])

  const handleSave = async () => {
    if (!canEdit || isSaving || !hasChanges) return
    setIsSaving(true)
    setError(null)
    setSavedMessage(null)
    try {
      if (activeWeekId === null) throw new Error('Не удалось определить неделю чек-листа.')
      await saveBusinessHealth(studioInternalId, activeWeekId, draft, record!.storage_scope)
      await refresh(activeWeekId ?? undefined)
      setSavedMessage('Новая версия чек-листа сохранена.')
    } catch (saveError) {
      setError(errorText(saveError))
    } finally {
      setIsSaving(false)
    }
  }

  const updateAnswer = (questionId: string, answer: BusinessHealthAnswer) => {
    setDraft(previous => ({ ...previous, [questionId]: answer }))
    setOpenAnswerMenu(null)
    setSavedMessage(null)
  }

  const toggleAnswerMenu = (questionId: string, button: HTMLButtonElement) => {
    if (openAnswerMenu?.questionId === questionId) {
      setOpenAnswerMenu(null)
      return
    }
    const rect = button.getBoundingClientRect()
    const opensUp = rect.bottom + ANSWER_MENU_HEIGHT + 4 > window.innerHeight - 8 && rect.top >= ANSWER_MENU_HEIGHT + 12
    setOpenAnswerMenu({
      questionId,
      left: Math.max(8, Math.min(rect.right - ANSWER_MENU_WIDTH, window.innerWidth - ANSWER_MENU_WIDTH - 8)),
      top: opensUp ? rect.top - ANSWER_MENU_HEIGHT - 4 : rect.bottom + 4,
    })
  }

  const renderAnswerControl = (question: BusinessHealthQuestion) => {
    const selectedAnswer = ANSWER_OPTIONS.find(option => option.value === draft[question.id])

    if (!canEdit) {
      return (
        <span className={`inline-flex h-7 shrink-0 items-center rounded-md border px-2 text-[11px] font-medium ${answerTone(draft[question.id])}`}>
          {selectedAnswer?.label ?? 'Не заполнено'}
        </span>
      )
    }

    return (
      <div className="shrink-0" data-business-health-answer-control>
        <button
          id={`business-health-answer-${question.id}`}
          type="button"
          aria-label={`Ответ на вопрос: ${question.text}. ${selectedAnswer?.label ?? 'Не заполнено'}`}
          aria-haspopup="listbox"
          aria-expanded={openAnswerMenu?.questionId === question.id}
          onClick={event => toggleAnswerMenu(question.id, event.currentTarget)}
          className={`inline-flex h-7 min-w-24 items-center justify-between gap-1.5 rounded-md border px-2 text-[11px] font-medium focus:outline-none focus:ring-2 focus:ring-primary ${answerTone(draft[question.id])}`}
        >
          {selectedAnswer?.label ?? 'Выбрать'}
          <ChevronDown aria-hidden="true" className="h-3 w-3 text-muted-foreground" />
        </button>
        {openAnswerMenu?.questionId === question.id && createPortal(
          <div
            data-business-health-answer-control
            role="listbox"
            aria-labelledby={`business-health-answer-${question.id}`}
            className="fixed z-[100] rounded-lg border border-border bg-card p-1 text-foreground shadow-lg"
            style={{ left: openAnswerMenu.left, top: openAnswerMenu.top, width: ANSWER_MENU_WIDTH }}
          >
            {ANSWER_OPTIONS.map(option => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={draft[question.id] === option.value}
                onClick={() => updateAnswer(question.id, option.value)}
                className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-xs text-foreground hover:bg-muted ${draft[question.id] === option.value ? 'bg-muted font-medium' : ''}`}
              >
                {option.label}
                {draft[question.id] === option.value && <Check className="h-3.5 w-3.5" />}
              </button>
            ))}
          </div>,
          document.body,
        )}
      </div>
    )
  }

  if (isLoading) {
    return <div className="flex min-h-[50vh] items-center justify-center text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Загружаем чек-лист…</div>
  }

  if (error && !record) {
    return <div className="mx-auto max-w-3xl p-4"><div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">{error}</div></div>
  }

  if (!record) return null

  return (
    <main className="mx-auto w-full max-w-3xl space-y-4 px-4 py-5 pb-10">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <PeriodSelector
            currentPeriod={selectedPeriod}
            onPrevPeriod={() => { if (selectedPeriodIndex >= 0) selectPeriod(record.periods[selectedPeriodIndex + 1]?.period_id) }}
            onNextPeriod={() => { if (selectedPeriodIndex > 0) selectPeriod(record.periods[selectedPeriodIndex - 1].period_id) }}
            canGoPrev={selectedPeriodIndex >= 0 && selectedPeriodIndex < record.periods.length - 1}
            canGoNext={selectedPeriodIndex > 0}
            isCurrentPeriod={!isHistoricalPeriodSelected}
            compact
            showViewModeSelector
            showPeriodLength={false}
            periodFilterValue={String(activeWeekId ?? '')}
            periodFilterOptions={periodWeeks.map(week => ({
              value: String(week.week_id),
              label: `Неделя ${week.week_number_in_period}`,
              disabled: !week.is_available,
            }))}
            onPeriodFilterChange={value => {
              if (!confirmDiscardDraft()) return
              setSelectedWeekId(Number(value))
              setSelectedReviewId(null)
              setSelectedPartnerReviewId(null)
            }}
          />
          <div className="mt-2">
            {activeWeek && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                {formatSavedAt(activeWeek.week_start)} – {formatSavedAt(activeWeek.week_end)} · {filledQuestionCount} из {totalQuestionCount} заполнено
              </p>
            )}
          </div>
        </div>
        {(weekHistory.length > 0 || partnerWeekHistory.length > 0) && (
        <div className="flex flex-wrap justify-end gap-2">
          {weekHistory.length > 0 && (
          <details className="group relative">
            <summary
              aria-label="История заполнений"
              className="flex cursor-pointer list-none items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground shadow-sm [&::-webkit-details-marker]:hidden"
            >
              <span>{selectedReview && selectedReviewId !== latestWeekReview?.review_id ? `Студия · ${formatSavedAt(selectedReview.created_at)}` : 'История студии'}</span>
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
            </summary>
            <div className="absolute right-0 z-30 mt-2 max-h-72 w-72 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-lg border border-border bg-card p-1 shadow-lg">
              <p className="px-3 py-2 text-xs font-medium text-muted-foreground">Студия · неделя {activeWeek?.week_number_in_period}</p>
              {weekHistory.map((review, index) => (
                <button
                  key={review.review_id}
                  type="button"
                  className={`w-full rounded-md px-3 py-2 text-left text-sm hover:bg-muted ${review.review_id === (selectedReviewId ?? latestWeekReview?.review_id) ? 'bg-muted' : ''}`}
                  onClick={event => {
                    if (!confirmDiscardDraft()) return
                    setSelectedReviewId(review.review_id)
                    setSelectedPeriodId(review.period_id)
                    setSelectedWeekId(review.week_id)
                    event.currentTarget.closest('details')?.removeAttribute('open')
                  }}
                >
                  <span className="block">{index === 0 ? 'Последняя версия · ' : ''}{formatSavedAt(review.created_at)} · Неделя {review.week_number_in_period}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">{review.author_name || 'Автор не указан'}</span>
                </button>
              ))}
              {record.can_edit && !isCurrentVersionSelected && (
                <p className="px-3 py-2 text-xs text-muted-foreground">Старая версия доступна только для просмотра.</p>
              )}
            </div>
          </details>
          )}
          {partnerWeekHistory.length > 0 && (
            <details className="group relative">
              <summary className="flex cursor-pointer list-none items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground shadow-sm [&::-webkit-details-marker]:hidden">
                <span>{selectedPartnerReview && selectedPartnerReviewId !== latestPartnerWeekReview?.review_id ? `Франчайзи · ${formatSavedAt(selectedPartnerReview.created_at)}` : 'История франчайзи'}</span>
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
              </summary>
              <div className="absolute right-0 z-30 mt-2 max-h-72 w-72 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-lg border border-border bg-card p-1 shadow-lg">
                <p className="px-3 py-2 text-xs font-medium text-muted-foreground">Франчайзи · неделя {activeWeek?.week_number_in_period}</p>
                {partnerWeekHistory.map((review, index) => (
                  <button key={review.review_id} type="button"
                    className={`w-full rounded-md px-3 py-2 text-left text-sm hover:bg-muted ${review.review_id === (selectedPartnerReviewId ?? latestPartnerWeekReview?.review_id) ? 'bg-muted' : ''}`}
                    onClick={event => {
                      if (!confirmDiscardDraft()) return
                      setSelectedPartnerReviewId(review.review_id)
                      event.currentTarget.closest('details')?.removeAttribute('open')
                    }}>
                    <span className="block">{index === 0 ? 'Последняя версия · ' : ''}{formatSavedAt(review.created_at)}</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">{review.author_name || 'Автор не указан'}</span>
                  </button>
                ))}
              </div>
            </details>
          )}
        </div>
        )}
      </div>

      {isHistoricalPeriodSelected && selectedPeriod && (
        <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          Период {formatSavedAt(selectedPeriod.period_start)} — {formatSavedAt(selectedPeriod.period_end)} доступен только для просмотра.
        </div>
      )}

      {record.storage_scope === 'legacy_studio' && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
          Общий блок франчайзи появится после миграции. Пока «Предприниматель» хранится и показывается отдельно в каждой студии.
        </div>
      )}

      {error && <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error}</div>}
      {savedMessage && <div role="status" className="flex items-center gap-2 rounded-lg border border-green-600/25 bg-green-600/5 p-3 text-sm text-green-700 dark:text-green-300"><Check className="h-4 w-4" />{savedMessage}</div>}

      {weekHistory.length === 0 && partnerWeekHistory.length === 0 && (
        <div className="rounded-lg border border-border bg-card px-3 py-2.5 text-sm text-muted-foreground">
          За выбранную неделю чек-лист ещё не сохраняли. Ответы и история появятся после первого сохранения.
        </div>
      )}

      <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2.5">
        <div>
          <p className="text-sm font-medium text-foreground">Баллы чек-листа</p>
        </div>
        <p aria-label={`Баллы чек-листа: ${formatPoints(totalPoints)} из ${totalQuestionCount}`} className="shrink-0 text-base font-semibold tabular-nums text-foreground">
          {formatPoints(totalPoints)} <span className="text-xs font-normal text-muted-foreground">/ {totalQuestionCount}</span>
        </p>
      </div>

      {BUSINESS_HEALTH_GROUPS.map(group => {
        const filledCount = group.questions.filter(question => draft[question.id] !== null && draft[question.id] !== undefined).length
        const groupPoints = group.questions.reduce((sum, question) => sum + answerPoints(draft[question.id]), 0)

        return (
        <div key={group.id} className="space-y-2">
          {group.id === 'team' && <h2 className="text-sm font-semibold text-foreground">Блоки студии</h2>}
          {group.id === 'entrepreneur' && record.storage_scope === 'partner' && (
            <div>
              <h2 className="text-sm font-semibold text-foreground">Общий блок франчайзи</h2>
              <p className="mt-1 text-xs text-muted-foreground">Ответы и история этого блока одинаковы во всех студиях франчайзи.</p>
            </div>
          )}
        <details className="group overflow-hidden rounded-xl border border-border bg-card">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 [&::-webkit-details-marker]:hidden">
            <span className="min-w-0 flex-1">
              <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{group.id === 'entrepreneur' && record.storage_scope === 'partner' ? 'Общий для всех студий франчайзи' : 'Оценка этой студии'}</span>
              <span className="block text-sm font-semibold text-foreground">{group.title}</span>
              <span className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                <span>Заполнено {filledCount} из {group.questions.length}</span>
                <span>Баллы {formatPoints(groupPoints)} / {group.questions.length}</span>
              </span>
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
          </summary>
          <div className="border-t border-border">
            <p className="px-4 pt-2 text-xs text-muted-foreground">{group.description}</p>
          <div className="divide-y divide-border sm:hidden">
            {groupQuestionsByMetric(group.questions).map((metricGroup, metricIndex) => (
              <div key={`${group.id}-mobile-metric-${metricIndex}`} className="px-3 py-2">
                {metricGroup.primaryMetric && (
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <h3 className="text-xs font-semibold text-foreground">{metricGroup.primaryMetric}</h3>
                    <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                      {formatPoints(metricGroup.questions.reduce((sum, question) => sum + answerPoints(draft[question.id]), 0))} / {metricGroup.questions.length}
                    </span>
                  </div>
                )}
                {metricGroup.questions.map(question => (
                  <div key={question.id} className="flex items-start justify-between gap-2 py-1.5">
                    <p className="min-w-0 flex-1 text-xs leading-4 text-foreground">{question.text}</p>
                    {renderAnswerControl(question)}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="hidden overflow-x-auto sm:block">
            <table className="w-full table-fixed border-collapse text-xs">
              <colgroup>
                <col className="w-[34%]" />
                <col />
              </colgroup>
              <tbody className="divide-y divide-border">
                {groupQuestionsByMetric(group.questions).map(metricGroup => (
                  metricGroup.questions.map((question, questionIndex) => (
                    <tr key={question.id}>
                      {questionIndex === 0 && (
                        <th
                          scope="rowgroup"
                          rowSpan={metricGroup.questions.length}
                          className="border-r border-border bg-muted/20 px-3 py-2 text-left align-middle font-semibold text-foreground"
                        >
                          <span className="flex flex-col gap-1">
                            <span>{metricGroup.primaryMetric ?? '—'}</span>
                            <span className="text-[10px] font-normal tabular-nums text-muted-foreground">
                              {formatPoints(metricGroup.questions.reduce((sum, item) => sum + answerPoints(draft[item.id]), 0))} / {metricGroup.questions.length} балл.
                            </span>
                          </span>
                        </th>
                      )}
                      <td className="px-3 py-2 align-middle">
                        <div className="flex items-center justify-between gap-3">
                          <p className="min-w-0 flex-1 leading-4 text-foreground">{question.text}</p>
                          {renderAnswerControl(question)}
                        </div>
                      </td>
                    </tr>
                  ))
                ))}
              </tbody>
            </table>
          </div>
          </div>
        </details>
        </div>
        )
      })}

      {canEdit && (
        <div className="sticky bottom-2 z-10 flex items-center justify-between gap-3 rounded-xl border border-border bg-card/95 p-3 shadow-lg backdrop-blur">
          <span className="text-xs text-muted-foreground">Сохранение создаст новую версию и сохранит предыдущую.</span>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={!hasChanges || isSaving}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Сохранить версию
          </button>
        </div>
      )}
    </main>
  )
}
